// Workspace › My CRM — an AM's own customers, follow-ups, quotes and work log. Managers can switch AM.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarClock, FileText, Users, BadgeCheck, PoundSterling, UserCog } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { useCan } from "../../../hooks/useAreaLevel";
import Person360Overlay from "../../../components/Person360Overlay";
import ActivityLogModal from "../../../components/activity/ActivityLogModal";
import VelocityTaskPopup, { followUpContext } from "../../crm/velocity/VelocityTaskPopup";
import FollowupsTab from "./FollowupsTab";
import CustomersTab from "./CustomersTab";
import QuotesTab from "./QuotesTab";
import WorkLogTab from "./WorkLogTab";
import { gbp } from "./_shared";

const TABS = [["followups", "Follow-ups"], ["customers", "My customers"], ["quotes", "My quotes"], ["worklog", "Work log"]];

function Kpi({ icon: Icon, label, value, sub, tone, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} className="ws-card text-left px-4 py-3 hover:border-white/25 transition-colors min-w-0" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-400"><Icon className={`w-3.5 h-3.5 ${tone}`} />{label}</div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
    </button>
  );
}

export default function MyCRM() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "followups";
  const amId = params.get("am") || "";
  const [ams, setAms] = useState({ can_switch: false, ams: [] });
  const [sum, setSum] = useState(null);
  const [p360, setP360] = useState(null);
  const [log, setLog] = useState(null);
  const [task, setTask] = useState(null);
  const [bump, setBump] = useState(0);
  const [sumBump, setSumBump] = useState(0);
  const canEdit = useCan("/workspace/crm")("edit");
  const set = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p, { replace: true }); };

  useEffect(() => { api.get("/my-crm/ams").then(({ data }) => setAms(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/my-crm/summary", { params: amId ? { am_id: amId } : {} }).then(({ data }) => setSum(data)).catch((e) => {
      toast.error(e?.response?.data?.detail || "Couldn't load that CRM view", { id: "mycrm-sum" });
      if (amId) set("am", ""); else setSum(null);
    });
  }, [amId, bump, sumBump]); // eslint-disable-line react-hooks/exhaustive-deps

  const amName = sum?.am?.name || user?.name;
  const f = sum?.followups || {};
  const toCtx = (r) => ({ id: r.id, name: r.name, am: amName, Status: r.velocity, LastOrderDate: r.last_order, DaysSinceLast: r.days_since, Spend12m: r.spend_12m, SpendLifetime: r.spend_life, OrdersLifetime: r.orders });
  const fu = params.get("fu") || "";
  const key = `${amId}-${bump}-${fu}`;
  const goFu = (b) => { const p = new URLSearchParams(params); p.set("tab", "followups"); p.set("fu", b); setParams(p, { replace: true }); };
  const common = { amId, on360: setP360, readOnly: !canEdit };

  return (
    <div className="space-y-5" data-testid="mycrm-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-amber-300">CRM work</div>
          <h2 className="text-lg font-bold text-white" data-testid="mycrm-title">{sum?.am && amId ? `${sum.am.name}'s CRM` : "My CRM"}</h2>
        </div>
        {ams.can_switch && (
          <label className="ml-auto inline-flex items-center gap-2 text-[12px] text-slate-300">
            <UserCog className="w-4 h-4 text-slate-400" />Viewing
            <select className="ws-input !w-auto" value={amId} onChange={(e) => set("am", e.target.value)} data-testid="mycrm-am-switch">
              <option value="">Me ({user?.name})</option>
              {ams.ams.filter((a) => a.id !== String(user?.id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="mycrm-kpis">
        <Kpi icon={AlertTriangle} tone="text-rose-300" label="Overdue" value={f.overdue || 0} sub="follow-ups past due" onClick={() => goFu("overdue")} testid="mycrm-kpi-overdue" />
        <Kpi icon={CalendarClock} tone="text-amber-300" label="Due" value={(f.today || 0) + (f.week || 0)} sub={`${f.today || 0} today · ${f.week || 0} this week`} onClick={() => goFu("today,week")} testid="mycrm-kpi-due" />
        <Kpi icon={Users} tone="text-sky-300" label="Customers" value={sum?.customers ?? "…"} sub={sum ? `${sum.velocity?.Cooling || 0} cooling · ${(sum.velocity?.Lapsed || 0) + (sum.velocity?.Dormant || 0)} lapsed/dormant` : ""} onClick={() => set("tab", "customers")} testid="mycrm-kpi-customers" />
        <Kpi icon={FileText} tone="text-amber-200" label="Open quotes" value={sum ? gbp(sum.open_quotes.value) : "…"} sub={sum ? `${sum.open_quotes.count} draft or sent` : ""} onClick={() => set("tab", "quotes")} testid="mycrm-kpi-quotes" />
        <Kpi icon={BadgeCheck} tone="text-teal-300" label="Trade sign-ups" value={sum?.signups_month ?? "…"} sub={sum ? `this month · ${sum.needs_trade_account} still need one` : ""} onClick={() => set("tab", "customers")} testid="mycrm-kpi-signups" />
        <Kpi icon={PoundSterling} tone="text-emerald-300" label="Last 30 days" value={sum ? gbp(sum.spend_30d) : "…"} sub={sum ? `${sum.orders_30d} order${sum.orders_30d === 1 ? "" : "s"}` : ""} onClick={() => set("tab", "customers")} testid="mycrm-kpi-spend" />
      </div>

      <div className="flex gap-1 border-b border-white/[0.06]" data-testid="mycrm-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => set("tab", k)} data-testid={`mycrm-tab-${k}`}
            className={`px-3 py-2 text-[12px] font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === k ? "border-amber-400 text-amber-200" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "followups" && <FollowupsTab key={key} {...common} initialBucket={fu} onChanged={() => setSumBump((x) => x + 1)} />}
      {tab === "customers" && <CustomersTab key={key} {...common} onLog={(r) => setLog(r)} onTask={(r) => setTask(toCtx(r))} />}
      {tab === "quotes" && <QuotesTab key={key} amId={amId} />}
      {tab === "worklog" && <WorkLogTab key={key} amId={amId} on360={setP360} />}

      {p360 && <Person360Overlay personId={p360} onClose={() => { setP360(null); setBump((x) => x + 1); }} />}
      {log && <ActivityLogModal personId={log.id} defaultAssigneeName={amName} contextLabel={log.name}
        draft={{ activity_type: "Call", subject: `Follow up · ${log.velocity || "customer"}`, content: followUpContext(toCtx(log)) }}
        onClose={() => setLog(null)} onSaved={() => { setLog(null); setBump((x) => x + 1); }} />}
      {task && <VelocityTaskPopup r={task} onClose={() => setTask(null)} />}
    </div>
  );
}
